import { useFormContext, Controller } from 'react-hook-form';
import { Box, InputAdornment, InputLabel, TextField, Typography } from '@mui/material';
import { inputStyles, errorInputBgStyles } from 'src/utils/style';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

export default function RHFTextField({
  styles,
  name,
  rules,
  helperText,
  type,
  displayName,
  sx,
  disabled = false,
  startAdornment = null,
  labelBold = false,
  defaultValue,
  max,
  min,
  allowDecimal = true,
  maxDecimals = 2,
  ...other
}) {
  const { control } = useFormContext();
  const disabledStyles = disabled
    ? {
        '& .MuiOutlinedInput-root': {
          ...inputStyles['& .MuiOutlinedInput-root'],
          backgroundColor: 'var(--p-color-bg-surface-disabled)',
          '& fieldset': {
            borderColor: 'var(--p-color-bg-surface-disabled) !important',
          },
        },
        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: 'var(--p-color-text)',
          color: 'var(--p-color-text)',
        },
      }
    : {};

  const labelBoldStyles = {
    color: 'text.primary',
    paddingBlockEnd: labelBold && 'var(--p-space-150)',
    display: labelBold && 'inline-block',
  };

  const formatNumberWithCommas = (num) => {
    if (num === '' || num === null || num === undefined) return '';
    const numStr = String(num);
    const parts = numStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const removeCommas = (str) => {
    return String(str).replace(/,/g, '');
  };

  const handleNumberChange = (event, field) => {
    const value = removeCommas(event.target.value);

    if (value === '') {
      field.onChange('');
      return;
    }

    const allowsNegative = !(min != null && Number(min) >= 0);
    const numericPattern = allowDecimal
      ? allowsNegative
        ? /^-?\d*\.?\d*$/
        : /^\d*\.?\d*$/
      : allowsNegative
        ? /^-?\d*$/
        : /^\d*$/;

    if (!numericPattern.test(value)) {
      return;
    }

    if (value === '-') {
      field.onChange(value);
      return;
    }

    if (allowDecimal && value.endsWith('.')) {
      field.onChange(value);
      return;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) {
      return;
    }

    if (max != null && max !== '' && numValue > Number(max)) {
      field.onChange(Number(max));
      return;
    }

    if (!value.includes('.')) {
      field.onChange(numValue);
    } else {
      const decimalPart = value.split('.')[1];
      if (decimalPart.length <= maxDecimals) {
        field.onChange(value);
      } else {
        const truncatedValue = `${value.split('.')[0]}.${decimalPart.slice(0, maxDecimals)}`;
        field.onChange(truncatedValue);
      }
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field, fieldState: { error } }) => (
        <Box sx={{ width: '100%', ...styles }}>
          <InputLabel sx={{ fontSize: labelBold && '0.8125rem', color: 'text.primary' }}>
            <Typography
              variant={labelBold ? 'body_md_bold' : 'body_md'}
              sx={labelBold ? labelBoldStyles : { color: 'text.primary' }}
            >
              {displayName}
            </Typography>
          </InputLabel>
          <TextField
            {...field}
            sx={{ ...inputStyles, ...errorInputBgStyles, ...sx, ...disabledStyles }}
            fullWidth
            type={type === 'number' ? 'text' : type}
            value={type === 'number' ? formatNumberWithCommas(field.value) : field.value}
            onChange={(event) => {
              if (type === 'number') {
                handleNumberChange(event, field);
              } else {
                field.onChange(event.target.value);
              }
            }}
            InputProps={{
              steps: 0.01,
              startAdornment: startAdornment && (
                <InputAdornment position="start">{startAdornment}</InputAdornment>
              ),
            }}
            error={!!error}
            helperText={
              error ? (
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  <ErrorOutlineOutlinedIcon sx={{ fontSize: '0.875rem' }} />
                  <span>{error?.message}</span>
                </Box>
              ) : (
                helperText
              )
            }
            size={'small'}
            variant={disabled ? 'filled' : 'outlined'}
            disabled={disabled}
            {...other}
          />
        </Box>
      )}
    />
  );
}
